import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue'
import { useImage } from '@/shared/composables/image'
import { mdiLeadPencil, mdiMinusCircleOutline, mdiPlus, mdiUpload } from '@mdi/js'

import EventEditActionButtons from '@/pages/CreateEventProcessingPage/ui/layout/EventEditActionButtons.vue'
import { eventApi, IEventEdit } from '@/enitites/event'
import { useEventUpdateMutation } from '@/enitites/event/api/queries'
import { IEventCarpet } from '@/enitites/event/model/types'


export default /*#__PURE__*/_defineComponent({
  __name: 'CreateEventDisplayMode',
  props: {
    eventData: null
  },
  emits: ["next", "back", "cancel"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'next'): void
  (e: 'back'): void
  (e: 'cancel'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  eventData: IEventEdit
};

const { getImageById, saveImage } = useImage()
const { error, isPending, mutate: updateEvent } = useEventUpdateMutation()





const scoreboardBgImage = ref(props.eventData.scoreboardBgImage)
const scoreboardLogo = ref(props.eventData.scoreboardLogo)
const inputBgImageRef = ref<HTMLInputElement | null>(null)
const inputLogoRef = ref<HTMLInputElement | null>(null)
const eventCarpets = ref<IEventCarpet[]>(props.eventData.carpets || [])
const isUpdatingCarpets = ref(false)

const errorMessage = ref<string>()
const isSavingBgImage = ref(false)
const isSavingLogo = ref(false)
const bgImageError = ref<string>()
const logoError = ref<string>()
const optimisticBgImage = ref<string>()
const optimisticLogo = ref<string>()

const srcScoreboardBgImage = computed(() => {
  if (optimisticBgImage.value) {
    return optimisticBgImage.value
  }
  return scoreboardBgImage.value ? getImageById(scoreboardBgImage.value, 800, 450) : ''
})

const srcLogo = computed(() => {
  if (optimisticLogo.value) {
    return optimisticLogo.value
  }
  return scoreboardLogo.value ? getImageById(scoreboardLogo.value, 800, 800) : ''
})

const isInfoFilledOut = computed(() => !!props.eventData.title)

async function saveBgImageToEvent(imageFile: File): Promise<void> {
  isSavingBgImage.value = true
  bgImageError.value = undefined

  try {
    const result = await saveImage(imageFile)
    if (result) {
      scoreboardBgImage.value = result
    } else {
      optimisticBgImage.value = undefined
      bgImageError.value = 'Failed to upload background image'
    }
  } catch (e) {
    optimisticBgImage.value = undefined
    bgImageError.value = (e as Error).message || 'Failed to upload background image'
  } finally {
    isSavingBgImage.value = false
  }
}

async function saveLogoToEvent(imageFile: File): Promise<void> {
  isSavingLogo.value = true
  logoError.value = undefined

  try {
    const result = await saveImage(imageFile)
    if (result) {
      scoreboardLogo.value = result
    } else {
      optimisticLogo.value = undefined
      logoError.value = 'Failed to upload logo'
    }
  } catch (e) {
    optimisticLogo.value = undefined
    logoError.value = (e as Error).message || 'Failed to upload logo'
  } finally {
    isSavingLogo.value = false
  }
}

async function handleAddCarpet() {
  const id = 0
  const newCarpetIndex = eventCarpets.value.length + 1
  eventCarpets.value.push({
    id,
    eventId: props.eventData.id,
    name: `Carpet ${newCarpetIndex}`,
  })
}

async function handleRemoveCarpet(index: number) {
  eventCarpets.value = eventCarpets.value.filter((_, i) => i !== index)
}

async function handleUpdateCarpetName(index: number, name: string) {
  eventCarpets.value[index].name = name
}

async function updateCarpets() {
  if (isUpdatingCarpets.value) return
  isUpdatingCarpets.value = true

  try {
    await eventApi.updateEventCarpets(props.eventData.id, eventCarpets.value)
  } catch (e) {
    console.error('Error updating carpets:', e)
  } finally {
    isUpdatingCarpets.value = false
  }
}

const isInitializingScoreboards = ref(false)
async function initScoreboards() {
  if (isInitializingScoreboards.value) return
  isInitializingScoreboards.value = true

  try {
    await eventApi.initEventScoreboards(props.eventData.id)
  } catch (e) {
    console.error('Error initializing scoreboards:', e)
  } finally {
    isInitializingScoreboards.value = false
  }
}

async function saveEventData(onSuccess: () => void) {
  if (isPending.value || isUpdatingCarpets.value) return
  errorMessage.value = undefined

  try {
    // Update carpets if needed
    if (eventCarpets.value.length) {
      await updateCarpets()
      await initScoreboards()
    }

    const payload = {
      ...props.eventData,
      scoreboardBgImage: scoreboardBgImage.value,
      scoreboardLogo: scoreboardLogo.value,
      carpets: eventCarpets.value,
    }

    updateEvent(payload, { onSuccess })
  } catch (err) {
    errorMessage.value = (err as Error).message || 'Failed to save event data'
  }
}

function handleNext() {
  saveEventData(() => {
    emit('next')
  })
}

function handleCancel() {
  saveEventData(() => {
    emit('cancel')
  })
}

function openBgImageDialog() {
  inputBgImageRef.value?.click()
}

function handlerBgImageChange(evt: Event): void {
  const filesImg = (evt.target as HTMLInputElement).files
  if (!filesImg?.length) return

  saveBgImageToEvent(filesImg[0])
  const reader = new FileReader()
  reader.onload = (event) => {
    optimisticBgImage.value = event.target?.result as string
  }
  reader.readAsDataURL(filesImg[0])
}

function openLogoDialog() {
  inputLogoRef.value?.click()
}

function handlerLogoChange(evt: Event): void {
  const filesImg = (evt.target as HTMLInputElement).files
  if (!filesImg?.length) return

  saveLogoToEvent(filesImg[0])
  const reader = new FileReader()
  reader.onload = (event) => {
    optimisticLogo.value = event.target?.result as string
  }
  reader.readAsDataURL(filesImg[0])
}

return { __sfc: true,getImageById, saveImage, error, isPending, updateEvent, props, emit, scoreboardBgImage, scoreboardLogo, inputBgImageRef, inputLogoRef, eventCarpets, isUpdatingCarpets, errorMessage, isSavingBgImage, isSavingLogo, bgImageError, logoError, optimisticBgImage, optimisticLogo, srcScoreboardBgImage, srcLogo, isInfoFilledOut, saveBgImageToEvent, saveLogoToEvent, handleAddCarpet, handleRemoveCarpet, handleUpdateCarpetName, updateCarpets, isInitializingScoreboards, initScoreboards, saveEventData, handleNext, handleCancel, openBgImageDialog, handlerBgImageChange, openLogoDialog, handlerLogoChange, mdiLeadPencil, mdiMinusCircleOutline, mdiPlus, mdiUpload, EventEditActionButtons }
}

})